import Swal from 'sweetalert2';
import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, MenuItem, Paper, Grid, FormControlLabel, Checkbox, Button } from '@mui/material';

const RolePermissionPage = () => {
  const [roles, setRoles] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [features, setFeatures] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [allowedTabs, setAllowedTabs] = useState([]);
  const [allowedFeatures, setAllowedFeatures] = useState([]);

  // Get propertyId from localStorage user
  const storedUser = JSON.parse(localStorage.getItem('user'));
  const propertyId = storedUser?.propertyId?.id || storedUser?.properties?.[0]?.propertyId?.id;

  useEffect(() => {
    setRoles(['admin', 'receptionist', 'event-coordinator', 'restaurant-manager', 'frontdesk-manager']);
    setTabs(['admin', 'calendar', 'promotion', 'room', 'hotelInfo', 'property', 'reservation', 'guest']);
    setFeatures([
      'modifyBooking',
      'cancelBooking',
      'noShowBooking',
      'editPropertyInfo',
      'addFacility',
      'editFacility',
      'deleteFacility',
      'addPolicy',
      'editPolicy',
      'deletePolicy',
      'addService',
      'editService',
      'deleteService',
      'addEvent',
      'editEvent',
      'deleteEvent',
      'addRestaurant',
      'editRestaurant',
      'deleteRestaurant',
      'addSpa',
      'editSpa',
      'deleteSpa'
    ]);
  }, []);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!selectedRole) return;
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/role-permissions/${selectedRole}?propertyId=${propertyId}`
      );
      try {
        if (res.ok) {
          const data = await res.json();
          setAllowedTabs(data.allowedTabs || []);
          setAllowedFeatures(data.allowedFeatures || []);
        } else {
          setAllowedTabs([]);
          setAllowedFeatures([]);
        }
      } catch (err) {
        console.error('Failed to parse JSON:', err);
        setAllowedTabs([]);
        setAllowedFeatures([]);
      }
    };
    fetchPermissions();
  }, [selectedRole, propertyId]);

  const handleSave = async () => {
    const structuredFeatures = {};

    const flatFeatures = Object.entries(allowedFeatures).flatMap(([_, obj]) =>
      Object.keys(obj).filter(key => obj[key])
    );
    flatFeatures.forEach(feature => {
      if (['modifyBooking', 'cancelBooking', 'noShowBooking'].includes(feature)) {
        structuredFeatures.reservation = {
          ...structuredFeatures.reservation,
          [feature]: true
        };
      } else if (['editPropertyInfo'].includes(feature)) {
        structuredFeatures.property = {
          ...structuredFeatures.property,
          [feature]: true
        };
      } else if (feature.includes('Facility')) {
        structuredFeatures.facilities = {
          ...structuredFeatures.facilities,
          [feature]: true
        };
      } else if (feature.includes('Policy')) {
        structuredFeatures.policies = {
          ...structuredFeatures.policies,
          [feature]: true
        };
      } else if (feature.includes('Service')) {
        structuredFeatures.services = {
          ...structuredFeatures.services,
          [feature]: true
        };
      } else if (feature.includes('Event')) {
        structuredFeatures.events = {
          ...structuredFeatures.events,
          [feature]: true
        };
      } else if (feature.includes('Restaurant')) {
        structuredFeatures.restaurants = {
          ...structuredFeatures.restaurants,
          [feature]: true
        };
      } else if (feature.includes('Spa')) {
        structuredFeatures.spa = {
          ...structuredFeatures.spa,
          [feature]: true
        };
      }
    });

    await fetch(`${process.env.REACT_APP_API_URL}/api/role-permissions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: selectedRole, allowedTabs, allowedFeatures: structuredFeatures, propertyId }),
    });

    Swal.fire({
      icon: 'success',
      title: 'Permissions Saved',
      text: 'The permission settings have been updated successfully.',
      confirmButtonColor: '#A58E63'
    });
  };

  const tabLabels = {
    admin: 'Admin Panel',
    calendar: 'Calendar View',
    promotion: 'Promotion Panel',
    room: 'Room Manager',
    hotelInfo: 'Hotel Information',
    property: 'Property Panel',
    reservation: 'Reservation Center',
    guest: 'Guest Information',
  };

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto', padding: 4 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Role & Permission Management
      </Typography>

      <Box sx={{ mb: 3 }}>
        <TextField
          select
          label="Select Role"
          value={selectedRole}
          onChange={e => setSelectedRole(e.target.value)}
          fullWidth
        >
          {roles.map(role => (
            <MenuItem key={role} value={role}>
              {role}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Paper elevation={1} sx={{ padding: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
          Allowed Tabs
        </Typography>

        <Grid container spacing={2}>
          {tabs.map(tab => (
            <Grid item xs={12} sm={6} key={tab}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allowedTabs.includes(tab)}
                    onChange={() =>
                      setAllowedTabs(prev =>
                        prev.includes(tab)
                          ? prev.filter(t => t !== tab)
                          : [...prev, tab]
                      )
                    }
                  />
                }
                label={tabLabels[tab] || tab}
              />
            </Grid>
          ))}
        </Grid>

        {/** Insert flattenedFeatures here */}
        {(() => {
          const flattenedFeatures = Object.entries(allowedFeatures).flatMap(([_, obj]) =>
            Object.keys(obj).filter(key => obj[key])
          );
          return (
            <>
              <Typography variant="subtitle1" fontWeight={500} sx={{ mt: 3, mb: 2 }}>
                Restricted Features
              </Typography>

              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3 }}>Reservation</Typography>
              <Grid container spacing={2}>
                {features.filter(f => ['modifyBooking', 'cancelBooking', 'noShowBooking'].includes(f)).map(feature => (
                  <Grid item xs={12} sm={6} key={feature}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={flattenedFeatures.includes(feature)}
                          onChange={() =>
                            setAllowedFeatures(prev => {
                              const newState = { ...prev };
                              const group =
                                feature.includes('Facility') ? 'facilities' :
                                feature.includes('Policy') ? 'policies' :
                                feature.includes('Service') ? 'services' :
                                feature.includes('Event') ? 'events' :
                                feature.includes('Restaurant') ? 'restaurants' :
                                feature.includes('Spa') ? 'spa' :
                                feature.includes('Booking') ? 'reservation' :
                                feature.includes('Property') ? 'property' :
                                null;

                              if (!group) return prev;

                              newState[group] = {
                                ...newState[group],
                                [feature]: !newState[group]?.[feature]
                              };

                              return newState;
                            })
                          }
                        />
                      }
                      label={
                        feature === 'modifyBooking'
                          ? 'Modify Booking'
                          : feature === 'cancelBooking'
                          ? 'Cancel Booking'
                          : 'No Show Button'
                      }
                    />
                  </Grid>
                ))}
              </Grid>

              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3 }}>Property</Typography>
              <Grid container spacing={2}>
                {features.filter(f => ['editPropertyInfo'].includes(f)).map(feature => (
                  <Grid item xs={12} sm={6} key={feature}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={flattenedFeatures.includes(feature)}
                          onChange={() =>
                            setAllowedFeatures(prev => {
                              const newState = { ...prev };
                              const group =
                                feature.includes('Facility') ? 'facilities' :
                                feature.includes('Policy') ? 'policies' :
                                feature.includes('Service') ? 'services' :
                                feature.includes('Event') ? 'events' :
                                feature.includes('Restaurant') ? 'restaurants' :
                                feature.includes('Spa') ? 'spa' :
                                feature.includes('Booking') ? 'reservation' :
                                feature.includes('Property') ? 'property' :
                                null;

                              if (!group) return prev;

                              newState[group] = {
                                ...newState[group],
                                [feature]: !newState[group]?.[feature]
                              };

                              return newState;
                            })
                          }
                        />
                      }
                      label="Edit Property Info"
                    />
                  </Grid>
                ))}
              </Grid>

              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3 }}>Hotel Information</Typography>
              <Grid container spacing={2}>
                {features.filter(f => f.includes('Facility') || f.includes('Policy') || f.includes('Service') || f.includes('Event') || f.includes('Restaurant') || f.includes('Spa')).map(feature => (
                  <Grid item xs={12} sm={6} key={feature}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={flattenedFeatures.includes(feature)}
                          onChange={() =>
                            setAllowedFeatures(prev => {
                              const newState = { ...prev };
                              const group =
                                feature.includes('Facility') ? 'facilities' :
                                feature.includes('Policy') ? 'policies' :
                                feature.includes('Service') ? 'services' :
                                feature.includes('Event') ? 'events' :
                                feature.includes('Restaurant') ? 'restaurants' :
                                feature.includes('Spa') ? 'spa' :
                                feature.includes('Booking') ? 'reservation' :
                                feature.includes('Property') ? 'property' :
                                null;

                              if (!group) return prev;

                              newState[group] = {
                                ...newState[group],
                                [feature]: !newState[group]?.[feature]
                              };

                              return newState;
                            })
                          }
                        />
                      }
                      label={
                        feature === 'addFacility'
                          ? 'Add Facility'
                          : feature === 'editFacility'
                          ? 'Edit Facility'
                          : feature === 'deleteFacility'
                          ? 'Delete Facility'
                          : feature === 'addPolicy'
                          ? 'Add Policy'
                          : feature === 'editPolicy'
                          ? 'Edit Policy'
                          : feature === 'deletePolicy'
                          ? 'Delete Policy'
                          : feature === 'addService'
                          ? 'Add Service'
                          : feature === 'editService'
                          ? 'Edit Service'
                          : feature === 'deleteService'
                          ? 'Delete Service'
                          : feature === 'addEvent'
                          ? 'Add Event'
                          : feature === 'editEvent'
                          ? 'Edit Event'
                          : feature === 'deleteEvent'
                          ? 'Delete Event'
                          : feature === 'addRestaurant'
                          ? 'Add Restaurant'
                          : feature === 'editRestaurant'
                          ? 'Edit Restaurant'
                          : feature === 'deleteRestaurant'
                          ? 'Delete Restaurant'
                          : feature === 'addSpa'
                          ? 'Add Spa'
                          : feature === 'editSpa'
                          ? 'Edit Spa'
                          : feature === 'deleteSpa'
                          ? 'Delete Spa'
                          : feature
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          );
        })()}
      </Paper>

      <Button variant="contained" onClick={handleSave}>
        Save Permissions
      </Button>
    </Box>
  );
};

export default RolePermissionPage;